"use client";

import { useDeferredValue, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA,
  TEXTAREA_PROSE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  DEFAULT_TO_UNICODE_OPTIONS,
  latexToUnicode,
  type ScriptFallback,
  type ToUnicodeOptions,
} from "@/lib/tool-kit/latex-dev/latex-to-unicode";
import {
  DEFAULT_TO_LATEX_OPTIONS,
  type ToLatexOptions,
  unicodeToLatex,
} from "@/lib/tool-kit/latex-dev/unicode-to-latex";

/** English strings. A translated content file overrides these through `ui`. */
export const LATEX_TO_UNICODE_CONVERTER_STRINGS = {
  modeLabel: "Direction",
  modes: {
    toUnicode: "LaTeX → Unicode",
    toLatex: "Unicode → LaTeX",
  },
  inputLabelToUnicode: "LaTeX to convert",
  inputLabelToLatex: "Unicode text to convert",
  placeholderToUnicode: "\\alpha + \\beta \\leq x^2, \\mathbb{R}, caf\\'e",
  placeholderToLatex: "α + β ≤ x², ℝ, café",
  hint: "Converted in your browser as you type. Nothing is uploaded.",
  loadExample: "Load the example",
  clear: "Clear",
  useAsInput: "Use the result as input and swap direction",
  exampleToUnicode:
    "The photon energy is $E = h\\nu$ and its wavelength is $\\lambda = \\frac{hc}{E}$.\nFor $x \\in \\mathbb{R}$ with $x \\geq 0$ we have $e^x \\geq 1 + x + \\frac{x^2}{2}$.\nThe caf\\'e in Fran\\c{c}ois's na\\\"ive plan uses $\\sqrt{2}$ and $\\hat{\\theta}_n \\to \\theta_0$.",
  exampleToLatex:
    "For x ∈ ℝ, α ≤ β gives x² ≤ y² when x ≥ 0 — a “first-order” result.\nWater is H₂O; the café near 45° north sells crème brûlée for 5 × 3 = 15 euros.\nThe set 𝒪(n²) of Σ-algebras is not ∅.",
  optionsHeading: "Options",
  stripDelimiters: "Remove the $ … $, $$ … $$ and \\( … \\) around formulas",
  ligatures: "Read `` '' -- --- as curly quotes and dashes",
  ligaturesBack: "Write curly quotes and dashes as `` '' -- ---",
  stripComments: "Remove % comments",
  scriptFallbackLabel: "A superscript or subscript with no Unicode form",
  scriptFallbacks: {
    keep: "Keep the LaTeX as typed",
    parens: "Write it in brackets, like x^(ab)",
  },
  wrapMath: "Put maths symbols in $ … $",
  escapeSpecials: "Escape # $ % & _ { } ~ ^ \\ so they print literally",
  results: "Result",
  outputLabel: "Converted text",
  copyResult: "Copy result",
  copied: "Copied!",
  emptyPrompt: "Enter some text, or load the example, to convert it.",
  converted: "Converted",
  characters: "Characters in result",
  leftAlone: "Left as typed",
  checkHeading: "Check the result",
  unconvertedNote:
    "one: {count} command has no Unicode equivalent and was left as typed: {items}.|other: {count} commands have no Unicode equivalent and were left as typed: {items}.",
  scriptsNote:
    "one: {count} superscript or subscript contains a character with no raised or lowered form, so it was not converted: {items}.|other: {count} superscripts or subscripts contain a character with no raised or lowered form, so they were not converted: {items}.",
  unmappedNote:
    "one: {count} character has no LaTeX command and was left as typed: {items}. Compile with XeLaTeX or LuaLaTeX, or add a command for it.|other: {count} characters have no LaTeX command and were left as typed: {items}. Compile with XeLaTeX or LuaLaTeX, or add commands for them.",
  allClear: "Nothing needed flagging.",
  preambleHeading: "Add to your preamble",
  noPackages: "No extra packages needed.",
  copyPreamble: "Copy preamble lines",
  fontNote:
    "If a symbol shows as an empty box, the font in use lacks it. Most system fonts cover Greek and the common operators; the rarer mathematical alphabets need a maths font such as STIX Two Math or Cambria Math.",
} as const;

type Mode = "toUnicode" | "toLatex";
type Strings = typeof LATEX_TO_UNICODE_CONVERTER_STRINGS;

const SCRIPT_FALLBACKS: readonly ScriptFallback[] = ["keep", "parens"];

interface Outcome {
  text: string;
  converted: number;
  /** Commands (to Unicode) or characters (to LaTeX) left as typed. */
  leftAlone: string[];
  scriptsKept: string[];
  packages: string[];
  unmapped: string[];
}

function runConversion(
  mode: Mode,
  source: string,
  toUnicode: ToUnicodeOptions,
  toLatex: ToLatexOptions
): Outcome {
  if (mode === "toUnicode") {
    const result = latexToUnicode(source, toUnicode);
    return {
      text: result.text,
      converted: result.converted,
      leftAlone: result.unconverted,
      scriptsKept: result.scriptsKept,
      packages: [],
      unmapped: [],
    };
  }
  const result = unicodeToLatex(source, toLatex);
  return {
    text: result.latex,
    converted: result.converted,
    leftAlone: result.unmapped,
    scriptsKept: [],
    packages: result.packages,
    unmapped: result.unmapped,
  };
}

export default function LatexToUnicodeConverterWidget() {
  const strings = useToolUi(LATEX_TO_UNICODE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("toUnicode");
  const [source, setSource] = useState("");
  const [toUnicode, setToUnicode] = useState<ToUnicodeOptions>(
    DEFAULT_TO_UNICODE_OPTIONS
  );
  const [toLatex, setToLatex] = useState<ToLatexOptions>(
    DEFAULT_TO_LATEX_OPTIONS
  );
  const deferred = useDeferredValue(source);
  const outcome =
    deferred.trim() === ""
      ? null
      : runConversion(mode, deferred, toUnicode, toLatex);

  function changeMode(next: Mode) {
    setMode(next);
    setSource("");
  }

  return (
    <section className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ltu-mode"
        onChange={changeMode}
        options={[
          { value: "toUnicode", label: strings.modes.toUnicode },
          { value: "toLatex", label: strings.modes.toLatex },
        ]}
        value={mode}
      />
      <TextAreaField
        hint={strings.hint}
        id="ltu-input"
        label={
          mode === "toUnicode"
            ? strings.inputLabelToUnicode
            : strings.inputLabelToLatex
        }
        onChange={setSource}
        placeholder={
          mode === "toUnicode"
            ? strings.placeholderToUnicode
            : strings.placeholderToLatex
        }
        prose={mode === "toLatex"}
        rows={8}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setSource(
              mode === "toUnicode"
                ? strings.exampleToUnicode
                : strings.exampleToLatex
            )
          }
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={outcome === null}
          onClick={() => {
            if (outcome) {
              setSource(outcome.text);
              setMode(mode === "toUnicode" ? "toLatex" : "toUnicode");
            }
          }}
          type="button"
        >
          {strings.useAsInput}
        </button>
      </div>
      {mode === "toUnicode" ? (
        <ToUnicodeOptionsPanel
          onChange={setToUnicode}
          options={toUnicode}
          strings={strings}
        />
      ) : (
        <ToLatexOptionsPanel
          onChange={setToLatex}
          options={toLatex}
          strings={strings}
        />
      )}
      <div aria-live="polite" className="space-y-4">
        {outcome === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <Results fmt={fmt} mode={mode} outcome={outcome} strings={strings} />
        )}
      </div>
    </section>
  );
}

function ToUnicodeOptionsPanel({
  options,
  onChange,
  strings,
}: {
  options: ToUnicodeOptions;
  onChange: (next: ToUnicodeOptions) => void;
  strings: Strings;
}) {
  const set = <K extends keyof ToUnicodeOptions>(
    key: K,
    value: ToUnicodeOptions[K]
  ) => onChange({ ...options, [key]: value });
  return (
    <Panel title={strings.optionsHeading}>
      <div className="flex flex-col gap-2">
        <Checkbox
          checked={options.stripMathDelimiters}
          label={strings.stripDelimiters}
          onChange={(value) => set("stripMathDelimiters", value)}
        />
        <Checkbox
          checked={options.ligatures}
          label={strings.ligatures}
          onChange={(value) => set("ligatures", value)}
        />
        <Checkbox
          checked={options.stripComments}
          label={strings.stripComments}
          onChange={(value) => set("stripComments", value)}
        />
      </div>
      <SelectField
        id="ltu-fallback"
        label={strings.scriptFallbackLabel}
        onChange={(value) => set("scriptFallback", value)}
        options={SCRIPT_FALLBACKS.map((value) => ({
          value,
          label: strings.scriptFallbacks[value],
        }))}
        value={options.scriptFallback}
      />
    </Panel>
  );
}

function ToLatexOptionsPanel({
  options,
  onChange,
  strings,
}: {
  options: ToLatexOptions;
  onChange: (next: ToLatexOptions) => void;
  strings: Strings;
}) {
  const set = <K extends keyof ToLatexOptions>(
    key: K,
    value: ToLatexOptions[K]
  ) => onChange({ ...options, [key]: value });
  return (
    <Panel title={strings.optionsHeading}>
      <div className="flex flex-col gap-2">
        <Checkbox
          checked={options.wrapMath}
          label={strings.wrapMath}
          onChange={(value) => set("wrapMath", value)}
        />
        <Checkbox
          checked={options.ligatures}
          label={strings.ligaturesBack}
          onChange={(value) => set("ligatures", value)}
        />
        <Checkbox
          checked={options.escapeSpecials}
          label={strings.escapeSpecials}
          onChange={(value) => set("escapeSpecials", value)}
        />
      </div>
    </Panel>
  );
}

function Results({
  outcome,
  mode,
  strings,
  fmt,
}: {
  outcome: Outcome;
  mode: Mode;
  strings: Strings;
  fmt: (value: number) => string;
}) {
  const locale = useToolLocale();
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.converted}
          value={fmt(outcome.converted)}
        />
        <StatCard
          label={strings.characters}
          value={fmt(Array.from(outcome.text).length)}
        />
        <StatCard
          label={strings.leftAlone}
          value={fmt(outcome.leftAlone.length + outcome.scriptsKept.length)}
        />
      </StatGrid>
      <div className="space-y-2">
        <label className={LABEL} htmlFor="ltu-output">
          {strings.outputLabel}
        </label>
        <textarea
          className={mode === "toUnicode" ? TEXTAREA_PROSE : TEXTAREA}
          id="ltu-output"
          readOnly
          rows={Math.min(14, outcome.text.split("\n").length + 2)}
          spellCheck={false}
          value={outcome.text}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={outcome.text}
      />
      {mode === "toLatex" ? (
        <Preamble packages={outcome.packages} strings={strings} />
      ) : null}
      <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
      <Checks locale={locale} mode={mode} outcome={outcome} strings={strings} />
      <p className={HINT}>{strings.fontNote}</p>
    </>
  );
}

function Preamble({
  packages,
  strings,
}: {
  packages: string[];
  strings: Strings;
}) {
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.preambleHeading}</h3>
      {packages.length > 0 ? (
        <>
          <pre className={`${TEXTAREA} min-h-0`}>{packages.join("\n")}</pre>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyPreamble}
            small
            text={packages.join("\n")}
          />
        </>
      ) : (
        <Notice>{strings.noPackages}</Notice>
      )}
    </div>
  );
}

function Checks({
  outcome,
  mode,
  strings,
  locale,
}: {
  outcome: Outcome;
  mode: Mode;
  strings: Strings;
  locale: string;
}) {
  const fmt = useToolNumber();
  const notes: string[] = [];
  const list = (items: string[]) => items.slice(0, 12).join("  ");
  if (outcome.leftAlone.length > 0) {
    const template =
      mode === "toUnicode" ? strings.unconvertedNote : strings.unmappedNote;
    notes.push(
      tPlural(template, locale, outcome.leftAlone.length, {
        count: fmt(outcome.leftAlone.length),
        items: list(outcome.leftAlone),
      }).trim()
    );
  }
  if (outcome.scriptsKept.length > 0) {
    notes.push(
      tPlural(strings.scriptsNote, locale, outcome.scriptsKept.length, {
        count: fmt(outcome.scriptsKept.length),
        items: list(outcome.scriptsKept),
      }).trim()
    );
  }
  if (notes.length === 0) {
    return <Notice>{strings.allClear}</Notice>;
  }
  return (
    <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
      {notes.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  );
}
